import { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import type { VehicleSocket } from "../../../../../store/admin/vehiclesSocketStore";
import { useTranslation } from "react-i18next";
import { useStatusesStore } from "../../../../../store/admin/statusesStore";
import { useCompaniesStore } from "../../../../../store/admin/companiesStore";
import { useUnitsStore } from "../../../../../store/admin/unitsStore";
import { useTrailersStore, getTrailerById } from "../../../../../store/admin/trailersStore";
import { getStatusOptionsForEntity, getStatusHumanLabel } from "../../../../../utils/getStatusEntity";
import { brand } from "../../../../../config/theme";

interface StatesContentProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
}

interface StatusOption {
  value: string;
  label: string;
}

type EntityType = "vehicle" | "trailer";

// Modal para crear estado personalizado
interface CreateCustomStatusModalProps {
  isDarkMode: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

function CreateCustomStatusModal({
  isDarkMode,
  onClose,
  onSuccess,
}: CreateCustomStatusModalProps) {
  const { t } = useTranslation(["lite", "adminSections"]);
  const [statusName, setStatusName] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { createStatus } = useStatusesStore();
  // Para resellers usar la compañía seleccionada, para carriers usar su propia compañía
  const selectedCompanyId = useCompaniesStore((state) => state.companyId);
  const userCompanyId = localStorage.getItem("userCompanyId");
  const companyId = selectedCompanyId || userCompanyId;

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const inputBg = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const hoverBg = isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100";

  const handleSubmit = async () => {
    if (!statusName.trim()) {
      setError(t("states.statusNameRequired", { ns: "lite", defaultValue: "El nombre del estado es requerido" }));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const result = await createStatus({
        name: statusName.trim(),
        active: true,
        company_id: companyId,
      });

      if (result.success) {
        Swal.fire({
          title: t("states.success", { ns: "lite", defaultValue: "Éxito" }),
          text: t("states.statusCreatedSuccess", { ns: "lite", defaultValue: "Estado creado correctamente" }),
          icon: "success",
          confirmButtonText: t("states.understood", { ns: "lite", defaultValue: "Entendido" }),
          confirmButtonColor: brand.primary,
        });
        onSuccess();
        onClose();
      } else {
        setError(result.error || t("states.createError", { ns: "lite", defaultValue: "Error al crear el estado" }));
      }
    } catch (err: any) {
      setError(t("states.createError", { ns: "lite", defaultValue: "Error al crear el estado" }));
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full p-8 border ${borderColor} shadow-2xl`}>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-20 h-20 rounded-full bg-primary-500 flex items-center justify-center mb-6">
              <i className="ri-refresh-line text-4xl text-black animate-spin"></i>
            </div>
            <h3 className={`font-semibold text-lg ${textColor} mb-2`}>
              {t("states.creatingStatus", { ns: "lite", defaultValue: "Creando estado" })}
            </h3>
            <p className={`text-center ${textSecondary} text-sm`}>
              {t("states.pleaseWait", { ns: "lite", defaultValue: "Por favor espere..." })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className={`${bgColor} rounded-xl max-w-md w-full overflow-hidden border ${borderColor} shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-add-line text-black text-lg"></i>
            </div>
            <h2 className={`font-semibold text-lg ${textColor}`}>
              {t("states.createCustomStatus", { ns: "lite", defaultValue: "Crear Estado Personalizado" })}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${hoverBg} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <div className="mb-4">
            <label className={`block text-sm ${textColor} mb-2`}>
              {t("states.statusName", { ns: "lite", defaultValue: "Nombre del estado" })} *
            </label>
            <input
              type="text"
              value={statusName}
              onChange={(e) => setStatusName(e.target.value)}
              placeholder={t("states.statusNamePlaceholder", { ns: "lite", defaultValue: "Ej: En reparación" })}
              className={`w-full px-4 py-3 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500`}
            />
          </div>

          <p className={`text-xs ${textSecondary} mb-4`}>
            {t("states.customStatusInfo", { ns: "lite", defaultValue: "Este estado personalizado estará disponible solo para tu compañía." })}
          </p>

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                <p className="text-sm text-red-500">{error}</p>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className={`px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} ${hoverBg} transition-colors font-medium`}
            >
              {t("units.form.cancel", { ns: "adminSections", defaultValue: "Cancelar" })}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!statusName.trim()}
              className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("states.create", { ns: "lite", defaultValue: "Crear" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface ChangeStatusModalProps {
  entityType: EntityType;
  entityId: string;
  entityName: string;
  isDarkMode: boolean;
  statusOptions: StatusOption[];
  currentStatusId: string | null;
  onClose: () => void;
  onSuccess: () => void;
}

function ChangeStatusModal({
  entityType,
  entityId,
  entityName,
  isDarkMode,
  statusOptions,
  currentStatusId,
  onClose,
  onSuccess,
}: ChangeStatusModalProps) {
  const { t } = useTranslation(["lite", "adminSections"]);
  const [selectedStatusId, setSelectedStatusId] = useState<string | null>(currentStatusId);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { updateVehicleStatus } = useUnitsStore();
  const { updateTrailerStatus } = useTrailersStore();

  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const cardBg = isDarkMode ? "bg-[#252525]" : "bg-gray-50";
  const hoverBg = isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100";

  const isVehicle = entityType === "vehicle";
  const icon = isVehicle ? "ri-car-line" : "ri-caravan-line";
  const entityLabel = isVehicle
    ? t("states.vehicle", { ns: "lite", defaultValue: "Vehículo" })
    : t("states.trailer", { ns: "lite", defaultValue: "Remolque" });

  const handleConfirm = async () => {
    if (!selectedStatusId || selectedStatusId === currentStatusId) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = isVehicle
        ? await updateVehicleStatus(entityId, selectedStatusId)
        : await updateTrailerStatus(entityId, selectedStatusId);

      if (result.success) {
        onSuccess();
        onClose();
      } else {
        setError(result.error || t("states.updateError", { ns: "lite", defaultValue: "Error al actualizar el estado" }));
      }
    } catch (err: any) {
      setError(t("states.updateError", { ns: "lite", defaultValue: "Error al actualizar el estado" }));
    } finally {
      setIsLoading(false);
    }
  };

  // Estado de carga
  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full p-8 border ${borderColor} shadow-2xl`}>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="w-20 h-20 rounded-full bg-primary-500 flex items-center justify-center mb-6">
              <i className="ri-refresh-line text-4xl text-black animate-spin"></i>
            </div>
            <h3 className={`font-semibold text-lg ${textColor} mb-2`}>
              {t("states.updatingStatus", { ns: "lite", defaultValue: "Actualizando estado" })}
            </h3>
            <p className={`text-center ${textSecondary} text-sm`}>
              {t("states.pleaseWait", { ns: "lite", defaultValue: "Por favor espere..." })}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div
        className={`${bgColor} rounded-xl max-w-md w-full overflow-hidden border ${borderColor} shadow-2xl`}
      >
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className={`${icon} text-black text-lg`}></i>
            </div>
            <h2 className={`font-semibold text-lg ${textColor}`}>
              {t("states.changeStatusOf", { ns: "lite", defaultValue: "Cambiar Estado de" })} {entityLabel}
            </h2>
          </div>
          <button
            onClick={onClose}
            className={`p-2 ${hoverBg} rounded-lg transition-colors`}
          >
            <i className={`ri-close-line text-xl ${textColor}`}></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Información de la entidad */}
          <div className={`p-4 rounded-lg ${cardBg} mb-4 space-y-2`}>
            <div className="flex items-center justify-between">
              <span className={textSecondary}>{entityLabel}:</span>
              <span className={`font-medium ${textColor}`}>{entityName}</span>
            </div>
          </div>

          {/* Lista de estados */}
          <div className="mb-4">
            <label className={`block text-sm ${textColor} mb-2`}>
              {t("states.selectStatus", { ns: "lite", defaultValue: "Seleccionar nuevo estado" })}
            </label>
            <div className={`max-h-60 overflow-y-auto rounded-lg border ${borderColor}`}>
              {statusOptions.length === 0 ? (
                <div className={`p-4 text-center ${textSecondary}`}>
                  {t("units.form.noStatusesAvailable", { ns: "adminSections", defaultValue: "No hay estados disponibles" })}
                </div>
              ) : (
                statusOptions.map((status) => {
                  const isSelected = selectedStatusId === status.value;
                  const isCurrent = currentStatusId === status.value;

                  return (
                    <button
                      key={status.value}
                      onClick={() => setSelectedStatusId(status.value)}
                      className={`w-full flex items-center justify-between p-3 border-b last:border-b-0 ${borderColor} transition-colors ${
                        isSelected
                          ? "bg-primary-500/20"
                          : hoverBg
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            isSelected
                              ? "border-primary-500 bg-primary-500"
                              : isDarkMode
                                ? "border-gray-500"
                                : "border-gray-300"
                          }`}
                        >
                          {isSelected && (
                            <i className="ri-check-line text-black text-xs"></i>
                          )}
                        </div>
                        <span className={`${textColor} text-sm`}>{status.label}</span>
                      </div>
                      {isCurrent && (
                        <span className={`text-xs px-2 py-0.5 rounded-full bg-primary-500/20 text-primary-500`}>
                          {t("states.current", { ns: "lite", defaultValue: "Actual" })}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {/* Error message */}
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30 mb-4">
              <div className="flex items-start gap-2">
                <i className="ri-error-warning-line text-red-500 mt-0.5"></i>
                <p className="text-sm text-red-500">{error}</p>
              </div>
            </div>
          )}

          {/* Botones */}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className={`px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} ${hoverBg} transition-colors font-medium`}
            >
              {t("units.form.cancel", { ns: "adminSections", defaultValue: "Cancelar" })}
            </button>
            <button
              onClick={handleConfirm}
              disabled={!selectedStatusId || selectedStatusId === currentStatusId}
              className="px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {t("units.form.save", { ns: "adminSections", defaultValue: "Guardar" })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function StatesContent({
  vehicle,
  isDarkMode,
}: StatesContentProps) {
  const { t } = useTranslation(["lite", "adminSections"]);
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const labelColor = isDarkMode ? "text-gray-500" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [modalEntityType, setModalEntityType] = useState<EntityType>("vehicle");
  const [showCreateStatusModal, setShowCreateStatusModal] = useState(false);

  // Loading states
  const [loading, setLoading] = useState(false);

  // Vehicle state
  const [vehicleStatusId, setVehicleStatusId] = useState<string | null>(null);
  const [vehicleStatusName, setVehicleStatusName] = useState<string | null>(null);

  // Trailer state
  const [trailerStatusId, setTrailerStatusId] = useState<string | null>(null);
  const [trailerStatusName, setTrailerStatusName] = useState<string | null>(null);
  const [trailerName, setTrailerName] = useState<string | null>(null);

  const { statuses, fetchStatuses } = useStatusesStore();
  const { getUnitByVehicleId } = useUnitsStore();

  // Detectar si tiene vehículo y/o remolque
  const hasVehicle = !!vehicle.vehicle_id;
  const hasTrailer = !!vehicle.remolque_id;

  // Para resellers usar la compañía seleccionada, para carriers usar su propia compañía
  const selectedCompanyId = useCompaniesStore((state) => state.companyId);
  const userCompanyId = useMemo(() => localStorage.getItem("userCompanyId"), []);
  const companyId = selectedCompanyId || userCompanyId;

  // Obtener opciones de estado filtradas para "units" y "trailers" (incluye personalizados)
  const vehicleStatusOptions = getStatusOptionsForEntity(statuses, "units", t, companyId);

  const trailerStatusOptions = getStatusOptionsForEntity(statuses, "trailers", t, companyId);

  // Cargar estados si no están cargados
  useEffect(() => {
    if (statuses.length === 0) {
      fetchStatuses({ limit: 100, page: 1 });
    }
  }, [statuses.length, fetchStatuses]);

  // Helper para obtener nombre de estado
  const getStatusName = (statusId: string | null): string | null => {
    if (!statusId || statuses.length === 0) return null;
    const status = statuses.find(s => s.id === statusId);
    if (status) {
      return getStatusHumanLabel(status.name, t);
    }
    return null;
  };

  // Obtener datos al ingresar
  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch vehicle data
      if (hasVehicle) {
        const result = await getUnitByVehicleId(vehicle.vehicle_id);
        if (result.success && result.data) {
          const statusId = result.data.status_id || null;
          setVehicleStatusId(statusId);
          setVehicleStatusName(getStatusName(statusId));
        }
      }

      // Fetch trailer data
      if (hasTrailer) {
        const result = await getTrailerById(vehicle.remolque_id!);
        if (result.success && result.data) {
          const statusId = result.data.status_id || null;
          setTrailerStatusId(statusId);
          setTrailerStatusName(getStatusName(statusId));
          setTrailerName(result.data.name || result.data.plate || "Remolque");
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasVehicle || hasTrailer) {
      fetchData();
    }
  }, [vehicle.vehicle_id, vehicle.remolque_id]);

  // Actualizar nombres cuando se cargan los statuses
  useEffect(() => {
    if (statuses.length > 0) {
      if (vehicleStatusId) {
        setVehicleStatusName(getStatusName(vehicleStatusId));
      }
      if (trailerStatusId) {
        setTrailerStatusName(getStatusName(trailerStatusId));
      }
    }
  }, [statuses, vehicleStatusId, trailerStatusId]);

  const handleOpenModal = (type: EntityType) => {
    setModalEntityType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const handleSuccess = () => {
    fetchData();
  };

  // GPS satellites check
  const hasGpsSatellites = vehicle.gps_sat != null && vehicle.gps_sat > 0;
  const gpsValue = hasGpsSatellites
    ? `${vehicle.gps_sat} ${t("states.satellites", { ns: "lite", defaultValue: "satélites" })}`
    : t("states.noSignal", { ns: "lite" });

  const states = [
    { label: t("states.engine", { ns: "lite" }), value: vehicle.ignition ? t("states.on", { ns: "lite" }) : t("states.off", { ns: "lite" }), active: vehicle.ignition },
    { label: t("states.gps", { ns: "lite" }), value: gpsValue, active: hasGpsSatellites },
    { label: t("states.battery", { ns: "lite" }), value: vehicle.battery != null ? `${vehicle.battery.toFixed(0)}%` : t("vehicleCard.noData", { ns: "lite" }), active: vehicle.battery != null && vehicle.battery > 20 },
    { label: t("states.movement", { ns: "lite" }), value: vehicle.speed != null && vehicle.speed > 0 ? t("states.moving", { ns: "lite" }) : t("states.stopped", { ns: "lite" }), active: vehicle.speed != null && vehicle.speed > 0 },
  ];

  // Loading state
  if (loading) {
    return (
      <div className={`p-3 border-t ${borderColor}`}>
        <h4 className="text-primary-500 font-semibold text-xs mb-2">
          {t("states.title", { ns: "lite" })}
        </h4>
        <div className="flex justify-center items-center py-6">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className={`p-3 border-t ${borderColor}`}>
      <h4 className="text-primary-500 font-semibold text-xs mb-2">
        {t("states.title", { ns: "lite" })}
      </h4>
      <div className="grid grid-cols-2 gap-2">
        {states.map((state, index) => (
          <div key={index} className="flex items-center gap-2">
            <div
              className={`w-2 h-2 rounded-full ${
                state.active ? "bg-primary-500" : "bg-red-500"
              }`}
            ></div>
            <div>
              <p className={`${labelColor} text-[9px]`}>{state.label}</p>
              <p className={`${textColor} text-[10px] font-medium`}>
                {state.value}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Estado del Vehículo */}
      {hasVehicle && (
        <div className={`mt-3 pt-3 border-t ${borderColor}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <i className={`ri-car-line text-[10px] ${labelColor}`}></i>
              <span className={`${labelColor} text-[9px]`}>
                {t("states.vehicleStatus", { ns: "lite", defaultValue: "Estado Vehículo" })}
              </span>
            </div>
            <span className={`${textColor} text-[10px] font-medium`}>
              {vehicleStatusName || t("states.noStatus", { ns: "lite", defaultValue: "Sin estado" })}
            </span>
          </div>
          <button
            onClick={() => handleOpenModal("vehicle")}
            className={`w-full px-4 py-2 rounded-lg bg-primary-500 text-black text-xs font-medium hover:bg-primary-400 transition-colors flex items-center justify-center gap-2`}
          >
            <i className="ri-exchange-line"></i>
            {t("states.changeVehicleStatus", { ns: "lite", defaultValue: "Cambiar Estado Vehículo" })}
          </button>
        </div>
      )}

      {/* Estado del Remolque */}
      {hasTrailer && (
        <div className={`mt-3 pt-3 border-t ${borderColor}`}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1">
              <i className={`ri-caravan-line text-[10px] ${labelColor}`}></i>
              <span className={`${labelColor} text-[9px]`}>
                {t("states.trailerStatus", { ns: "lite", defaultValue: "Estado Remolque" })}
              </span>
            </div>
            <span className={`${textColor} text-[10px] font-medium`}>
              {trailerStatusName || t("states.noStatus", { ns: "lite", defaultValue: "Sin estado" })}
            </span>
          </div>
          <button
            onClick={() => handleOpenModal("trailer")}
            className={`w-full px-4 py-2 rounded-lg bg-blue-500 text-white text-xs font-medium hover:bg-blue-400 transition-colors flex items-center justify-center gap-2`}
          >
            <i className="ri-exchange-line"></i>
            {t("states.changeTrailerStatus", { ns: "lite", defaultValue: "Cambiar Estado Remolque" })}
          </button>
        </div>
      )}

      {/* Si no tiene ni vehículo ni remolque */}
      {!hasVehicle && !hasTrailer && (
        <div className={`mt-3 pt-3 border-t ${borderColor}`}>
          <p className={`${labelColor} text-[10px] text-center`}>
            {t("states.noEntities", { ns: "lite", defaultValue: "No hay vehículo o remolque asociado" })}
          </p>
        </div>
      )}

      {/* Botón para crear estado personalizado */}
      <div className={`mt-3 pt-3 border-t ${borderColor}`}>
        <button
          onClick={() => setShowCreateStatusModal(true)}
          className={`w-full px-4 py-2 rounded-lg border ${borderColor} ${textColor} text-xs font-medium hover:bg-primary-500 hover:text-black hover:border-primary-500 transition-colors flex items-center justify-center gap-2`}
        >
          <i className="ri-add-line"></i>
          {t("states.createCustomStatus", { ns: "lite", defaultValue: "Crear Estado Personalizado" })}
        </button>
      </div>

      {/* Modal para crear estado personalizado */}
      {showCreateStatusModal && (
        <CreateCustomStatusModal
          isDarkMode={isDarkMode}
          onClose={() => setShowCreateStatusModal(false)}
          onSuccess={() => {
            fetchStatuses({ limit: 100, page: 1 });
          }}
        />
      )}

      {/* Modal */}
      {showModal && (
        <ChangeStatusModal
          entityType={modalEntityType}
          entityId={modalEntityType === "vehicle" ? vehicle.vehicle_id : vehicle.remolque_id!}
          entityName={modalEntityType === "vehicle" ? (vehicle.vehicle.name || vehicle.fullId) : (trailerName || "Remolque")}
          isDarkMode={isDarkMode}
          statusOptions={modalEntityType === "vehicle" ? vehicleStatusOptions : trailerStatusOptions}
          currentStatusId={modalEntityType === "vehicle" ? vehicleStatusId : trailerStatusId}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
